import React, { useEffect, useMemo, useState } from "react";
import { useTranslate } from "../../../../../customHooks";
import { useLazyGetRulesByRuleStackQuery } from "../../../../../redux/services/rules-service";
import { toast } from "../../../../../components";
import { LoadingIcon, TruncateText } from "@panwds/react-ui";
import Emitter from "../../../../../eventEmitter";
import { dataProvider } from "../../../../../dataProvider";
import * as DataTypes from "../../../../../api/FwaasDataTypes";
import { renderStatusWithCommit } from "../../../firewallsUtil";

interface SecurityPoliciesProps {
    firewall: Record<string, any>;
    status: Record<string, any>;
    managedBy: string;
    styles: any;
}

const SecurityPoliciesOverview: React.FC<SecurityPoliciesProps> = ({
    firewall,
    status,
    managedBy,
    styles,
}) => {
    const translate = useTranslate();
    const isRuleStack = managedBy === "Local Rulestack";
    const isSCMOrPanorama = managedBy === "SCM" || managedBy === "Panorama";
    const isPanorama = managedBy === "Panorama";
    const isSCM = managedBy === "SCM";
    const [totalRules, setTotalRules] = useState<number>(0);
    let [trigger, { data, error, isLoading: rulesLengthLoading }] =
        useLazyGetRulesByRuleStackQuery();
    const Region =
        useMemo(
            () => new URLSearchParams(location.search).get("region"),
            [location.search]
        ) || undefined;
    const [links, setLinks] = useState<any>({});

    const renderRuleStackStatus = (statusValue: string | undefined, commitInfo: any) => {
        if (rulesLengthLoading) {
            return <LoadingIcon size="sm" className="tw-animate-spin" />;
        }

        return renderStatusWithCommit(statusValue, commitInfo);
    };

    const renderNumberOfRules = () => {
        if (rulesLengthLoading) {
            return <LoadingIcon size="sm" className="tw-animate-spin" />;
        }

        if (totalRules !== 0) {
            return (
                <a
                    href={`/rulestacks/${firewall?.RuleStackName}/rules`}
                    className="text-blue-500"
                >
                    {totalRules}
                </a>
            );
        }

        return totalRules;
    };

    const fetchAllRules = async (nextToken?: string) => {
        try {
            data = await trigger({
                RuleStackName: firewall?.RuleStackName,
                RuleListName: "LocalRule",
                NextToken: nextToken,
                Region,
            }).unwrap();
            const newRulesCount = data.ruleEntries.length;
            setTotalRules((prevCount: any) => prevCount + newRulesCount);

            if (data.nextToken) {
                fetchAllRules(data.nextToken);
            }
        } catch (error: any) {
            toast.error(`${error?.code}: ${error?.error}`, {
                toastId: "security-policies-overview-rules-list",
            });
        }
    };

    const loadLinkData = () => {
        let apiCall: { resource: string; url?: string; payload?: any } = {
            resource: "settings",
            payload: { panorama: true },
        };
        if (import.meta.env.VITE_SHOW_CM === "true") {
            apiCall = {
                resource: "integrations",
            };
        }

        dataProvider
            .describe(
                apiCall.resource,
                apiCall.url || "",
                apiCall?.payload || {}
            )
            .then(async (response: DataTypes.IFwaasApiResponse) => {
                setLinks(response?.data?.Links);
            })
            .catch((e: any) => {
                toast.error(e?.error?.error, { toastId: "support-describe" });
            });
    };

    useEffect(() => {
        if (isSCM) {
            loadLinkData();
        }
    }, [isSCM]);

    const getUrlLink = (LinkId: string) => {
        const url = links?.[LinkId]?.CloudManager?.Url;
        return url;
    };

    useEffect(() => {
        if (isRuleStack) {
            fetchAllRules();
        }
    }, [isRuleStack]);

    const handleRuleStackClick = (ruleStackName: string, type: string) => {
        const region = new URLSearchParams(window.location.search).get(
            "region"
        );
        if (region) {
            localStorage.setItem("currentRegion", region); // Store the region in localStorage
        }
        Emitter.emit("regionChanged", true); // Emit the region change event
        if (type === "local") {
            window.location.href = `/rulestacks/${ruleStackName}`;
        } else if (type === "global") {
            window.location.href = `/rulestacks`;
        }
    };

    return (
        <div className={styles.flexContainer}>
            <h2 className={styles.title}>
                {translate(`resources.firewallsV2.policyManager`)}
            </h2>
            <div className={styles.flexColumn}>
                <div className={styles.flexRow}>
                    <strong className={styles.flexRowHeader}>
                        {translate(`resources.firewallsV2.managedBy`)}
                    </strong>
                    <span className={styles.flexRowValues}>{managedBy}</span>
                </div>
                {isRuleStack && (
                    <>
                        <div className={styles.flexRow}>
                            <strong className={styles.flexRowHeader}>
                                {translate(
                                    `resources.firewallsV2.localRulestack`
                                )}
                            </strong>
                            <span className={styles.flexRowValues}>
                                {firewall?.RuleStackName ? (
                                    <a
                                        href="#"
                                        onClick={() =>
                                            handleRuleStackClick(
                                                firewall?.RuleStackName,
                                                "local"
                                            )
                                        }
                                        className="text-blue-500"
                                    >
                                        {firewall?.RuleStackName}
                                    </a>
                                ) : (
                                    "N/A"
                                )}
                            </span>
                        </div>
                        <div className={styles.flexRow}>
                            <strong className={styles.flexRowHeader}>
                                {translate(
                                    `resources.firewallsV2.localRulestackStatus`
                                )}
                            </strong>
                            <span className={styles.flexRowValues}>
                                {renderRuleStackStatus(status?.RuleStackStatus, status?.RuleStackCommitInfo)}
                            </span>
                        </div>
                        <div className={styles.flexRow}>
                            <strong className={styles.flexRowHeader}>
                                {translate(
                                    `resources.firewallsV2.numberOfRules`
                                )}
                            </strong>
                            <span className={styles.flexRowValues}>
                                {renderNumberOfRules()}
                            </span>
                        </div>
                    </>
                )}
                {isSCMOrPanorama && (
                    <>
                        <div className={styles.flexRow}>
                            <strong className={styles.flexRowHeader}>
                                {translate(`resources.firewallsV2.linkId`)}
                            </strong>
                            <TruncateText addClassName={styles.flexRowValues}>
                                {firewall?.LinkId}
                            </TruncateText>
                        </div>
                        <div className={styles.flexRow}>
                            <strong className={styles.flexRowHeader}>
                                {translate(`resources.firewallsV2.linkStatus`)}
                            </strong>
                            <TruncateText addClassName={styles.flexRowValues}>
                                {firewall?.LinkStatus}
                            </TruncateText>
                        </div>
                    </>
                )}
                {isSCMOrPanorama && <>
                    {isPanorama && <div className={styles.flexRow}>
                        <strong className={styles.flexRowHeader}>
                            {translate(`resources.firewallsV2.globalRulestack`)}
                        </strong>
                        <span className={styles.flexRowValues}>
                            <span className={styles.flexRowValues}>
                                {firewall?.GlobalRuleStackName ? (
                                    <a
                                        href="#"
                                        onClick={() =>
                                            handleRuleStackClick(
                                                firewall?.GlobalRuleStackName,
                                                "global"
                                            )
                                        }
                                        className="text-blue-500"
                                    >
                                        {firewall?.GlobalRuleStackName}
                                    </a>
                                ) : (
                                    "N/A"
                                )}
                            </span>
                        </span>
                    </div>}
                    <div className={styles.flexRow}>
                        <strong className={styles.flexRowHeader}>
                            {translate(
                                `resources.firewallsV2.globalRulestackStatus`
                            )}
                        </strong>
                        <span className={styles.flexRowValues}>
                            {renderRuleStackStatus(status?.GlobalRuleStackStatus, status?.GlobalRuleStackCommitInfo)}
                        </span>
                    </div>
                </>}
            </div>
        </div>
    );
};

export default SecurityPoliciesOverview;
